import React from 'react';
import PropTypes from 'prop-types';
import './UploadStatus.css';

/**
 * UploadStatus Component
 * Displays upload status, results, and messages
 */
const UploadStatus = ({ isLoading, result, error, uploadProgress }) => {
  // Don't render anything if there's no status to show
  if (!isLoading && !result && !error) {
    return null;
  }

  return (
    <div className="upload-status-container">
      {/* Loading State */}
      {isLoading && (
        <div className="status-message status-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <div className="status-content">
            <h3 className="status-title">
              {uploadProgress > 0 ? 'Uploading...' : 'Processing your file...'}
            </h3>
            <p className="status-text">
              {uploadProgress > 0
                ? 'Sending email data to the server...'
                : 'Please wait while we extract and upload the email addresses.'}
            </p>
            {uploadProgress > 0 && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success State */}
      {!isLoading && result && !error && (
        <div className="status-message status-success">
          <div className="status-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="status-content">
            <h3 className="status-title">Upload successful!</h3>
            <p className="status-text">Your emails have been processed.</p>
            <div className="status-stats">
              <div className="stat-item">
                <span className="stat-value">{result.processed}</span>
                <span className="stat-label">Processed</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{result.stored}</span>
                <span className="stat-label">Stored</span>
              </div>
              {result.failed > 0 && (
                <div className="stat-item">
                  <span className="stat-value">{result.failed}</span>
                  <span className="stat-label">Failed</span>
                </div>
              )}
              {result.duplicates > 0 && (
                <div className="stat-item">
                  <span className="stat-value">{result.duplicates}</span>
                  <span className="stat-label">Duplicates</span>
                </div>
              )}
            </div>

            {/* Display stored emails */}
            {result.stored_emails && result.stored_emails.length > 0 && (
              <div className="stored-emails-section">
                <h4 className="stored-emails-title">Stored Emails:</h4>
                <div className="stored-emails-list">
                  {result.stored_emails.map((record) => (
                    <div key={record.email} className="stored-email-item">
                      <span className="stored-email-primary">{record.email}</span>
                      {record.compareEmail && (
                        <span className="stored-email-compare">
                          {' '}→ {record.compareEmail}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.failed > 0 && (
              <p className="status-note">
                {result.failed} email{result.failed > 1 ? 's' : ''} failed to store. Please try again.
              </p>
            )}
            {result.duplicates > 0 && (
              <p className="status-note">
                {result.duplicates} email{result.duplicates > 1 ? 's were' : ' was'} already in the database.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="status-message status-error">
          <div className="status-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <div className="status-content">
            <h3 className="status-title">Upload failed</h3>
            <p className="status-text">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

UploadStatus.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  result: PropTypes.shape({
    processed: PropTypes.number,
    stored: PropTypes.number,
    failed: PropTypes.number,
    duplicates: PropTypes.number,
    errors: PropTypes.array,
    stored_emails: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string.isRequired,
      compareEmail: PropTypes.string
    }))
  }),
  error: PropTypes.string,
  uploadProgress: PropTypes.number
};

UploadStatus.defaultProps = {
  uploadProgress: 0
};

export default UploadStatus;
